import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useSelector } from 'react-redux';
import './styles/table.css';
import Login from './pages/login/Login';
import Register from './pages/register/Register';
import PrivateRoute from './components/PrivateRoute';
import AuthenticatedRoute from './components/Auth/AuthenticatedRoute';
import Home from './pages/home/Home';
import Categories from './pages/categories/Categories';
import Users from './pages/admin/users/Users';
import Companies from './pages/admin/companies/Companies';
import Profile from './pages/profile/Profile';
import Achievements from './pages/Achievements';
import CompanyInformation from './pages/company/CompanyInformation';
import Agents from './pages/agents/Agents';
import Layout from './components/Layout';
import Reports from './pages/Reports';
import Disputes from './pages/Disputes';
import DisputeDetail from './pages/DisputeDetail';
import Feedback from './pages/Feedback';
import FeedbackThankYou from './pages/FeedbackThankYou';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password/ResetPassword';
import ForceChangePassword from './pages/force-change-password/ForceChangePassword';
import TermsAndConditions from './pages/terms-and-conditions';
import PrivacyPolicy from './pages/privacy-policy';
import FAQ from './pages/faq';
import Sitemap from './pages/sitemap';
import HowItWorks from './pages/how-it-works';
import AboutUs from './pages/about';
import Pricing from './pages/pricing';
import NotFound from './pages/404';

// Auction Components
import AuctionList from './components/Auction/AuctionList';
import AuctionDetail from './components/Auction/AuctionDetail';

// Results Pages
import CategoryResults from './pages/category-results/CategoryResults';
import SearchResults from './pages/search-results/SearchResults';
import LotDetail from './components/Auction/LotDetail';
import CreateEditAuction from './components/Auction/CreateEditAuction';
import EasyAuctionWizard from './components/Auction/Wizard/EasyAuctionWizard';
import UserAuctions from './components/Auction/UserAuctions';
import LotManagement from './components/Auction/LotManagement';
import MyLots from './components/Auction/MyLots';
import CreateEditLot from './components/Auction/CreateEditLot';
import BulkUploadLots from './components/Auction/BulkUploadLots';
import CategoryManagement from './components/Admin/CategoryManagement';
import AdminAuctionManagement from './components/Admin/AdminAuctionManagement';
import AdminDisputes from './pages/admin/disputes/AdminDisputes';
import AdminFeedback from './pages/admin/feedback/AdminFeedback';
import Dashboard from './pages/dashboard/Dashboard';
import FavoriteAuctions from './components/Auction/FavoriteAuctions';
import FavoriteLots from './components/Auction/FavoriteLots';
import AdminDashboard from './pages/dashboard/AdminDashboard';
// Live Auction Components
import LiveAuctions from './pages/LiveAuctions';
import LiveAuctionDetail from './pages/LiveAuctionDetail';
import BrowseLots from './pages/BrowseLots';
import FeaturedLotsPage from './pages/FeaturedLotsPage';
import FeaturedAuctionsPage from './pages/FeaturedAuctionsPage';
import UserAnalytics from './components/Dashboard/UserAnalytics';
import AuctionInvitation from './components/Auction/AuctionInvitation';
import PendingInvitations from './components/Auction/PendingInvitations';
import AuctionImages from './components/Auction/AuctionImages';
import LotImages from './components/Auction/LotImages';
import AuctionInvitationPage from './components/Auction/AuctionInvitationPage';
import AcceptedInvitations from './components/Auction/AcceptedInvitations';
import SentInvitations from './components/Auction/SentInvitations';
import PendingApproval from './components/Auth/PendingApproval';
import NotificationPreferences from './pages/notifications/NotificationPreferences';

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function PendingApprovalWrapper() {
    const { currentUser } = useSelector((state) => state.user);

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    return <PendingApproval user={currentUser} />;
}

const App = () => {
    const { currentUser } = useSelector((state) => state.user);
    const isAdmin = currentUser?.roles?.includes('admin');

    return (
        <ConfigProvider>
            <Router>
                <ScrollToTop />
                <PWAInstallPrompt />
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/pending-approval" element={<PendingApprovalWrapper />} />
                    <Route path="/change-password-required" element={<ForceChangePassword />} />

                    {/* Protected Routes */}
                    <Route element={<Layout />}>
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/sitemap" element={<Sitemap />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/about" element={<AboutUs />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/" element={<AuctionList />} />
                        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                        <Route path="/categories" element={<Categories />} />
                        <Route path="/auctions" element={<AuctionList />} />
                        <Route path="/category-results" element={<CategoryResults />} />
                        <Route path="/search-results" element={<SearchResults />} />
                        <Route path="/auctions/create" element={<PrivateRoute><CreateEditAuction /></PrivateRoute>} />
                        <Route path="/auctions/easy-create" element={<PrivateRoute><EasyAuctionWizard /></PrivateRoute>} />
                        <Route path="/auctions/:slug" element={<AuctionDetail />} />
                        <Route path="/auctions/:slug/edit" element={<PrivateRoute><CreateEditAuction /></PrivateRoute>} />
                        <Route path="/auctions/:slug/images" element={<PrivateRoute><AuctionImages /></PrivateRoute>} />
                        <Route path="/auctions/:auctionSlug/lots" element={<PrivateRoute><LotManagement /></PrivateRoute>} />
                        <Route path="/auctions/:auctionSlug/lots/:lotSlug" element={<LotDetail />} />
                        <Route path="/auctions/:auctionSlug/lots/:lotSlug/edit" element={<PrivateRoute><CreateEditLot /></PrivateRoute>} />
                        <Route path="/auctions/:auctionSlug/lots/:lotSlug/images" element={<PrivateRoute><LotImages /></PrivateRoute>} />
                        <Route path="/lots/create" element={<PrivateRoute><CreateEditLot /></PrivateRoute>} />
                        <Route path="/lots/bulk-upload" element={<PrivateRoute><BulkUploadLots /></PrivateRoute>} />
                        <Route path="/profile" element={<AuthenticatedRoute><Profile /></AuthenticatedRoute>} />
                        <Route path="/achievements" element={<AuthenticatedRoute><Achievements /></AuthenticatedRoute>} />
                        <Route path="/notifications" element={<AuthenticatedRoute><NotificationPreferences /></AuthenticatedRoute>} />
                        <Route path="/company" element={<AuthenticatedRoute><CompanyInformation /></AuthenticatedRoute>} />
                        <Route path="/agents" element={<AuthenticatedRoute><Agents /></AuthenticatedRoute>} />
                        <Route path="/my-auctions" element={<PrivateRoute><UserAuctions /></PrivateRoute>} />
                        <Route path="/my-lots" element={<PrivateRoute><MyLots /></PrivateRoute>} />
                        <Route path="/favorite-auctions" element={<PrivateRoute><FavoriteAuctions /></PrivateRoute>} />
                        <Route path="/favorite-lots" element={<PrivateRoute><FavoriteLots /></PrivateRoute>} />

                        {/* Live Auction Routes */}
                        <Route path="/live-auctions" element={<LiveAuctions />} />
                        <Route path="/live-auctions/:slug" element={<LiveAuctionDetail />} />

                        {/* Browse Lots Route */}
                        <Route path="/browse-lots" element={<BrowseLots />} />
                        <Route path="/featured-lots" element={<FeaturedLotsPage />} />
                        <Route path="/featured-auctions" element={<FeaturedAuctionsPage />} />

                        {/* Admin Routes */}
                        {isAdmin && (
                            <>
                                <Route path="/admin/dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
                                <Route path="/admin/users" element={<PrivateRoute><Users /></PrivateRoute>} />
                                <Route path="/admin/companies" element={<PrivateRoute><Companies /></PrivateRoute>} />
                                <Route path="/admin/categories" element={<PrivateRoute><CategoryManagement /></PrivateRoute>} />
                                <Route path="/admin/auctions" element={<PrivateRoute><AdminAuctionManagement /></PrivateRoute>} />
                                <Route path="/admin/disputes" element={<PrivateRoute><AdminDisputes /></PrivateRoute>} />
                                <Route path="/admin/feedback" element={<PrivateRoute><AdminFeedback /></PrivateRoute>} />
                            </>
                        )}

                        {/* Reports Route */}
                        <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />

                        {/* Disputes Route - Accessible to all, but filing requires login */}
                        <Route path="/disputes" element={<Disputes />} />
                        <Route path="/disputes/:disputeNumber" element={<PrivateRoute><DisputeDetail /></PrivateRoute>} />

                        {/* Feedback Route - Accessible to all, but submission requires login */}
                        <Route path="/feedback" element={<Feedback />} />
                        <Route path="/feedback/thank-you" element={<PrivateRoute><FeedbackThankYou /></PrivateRoute>} />

                        {/* User Analytics Route */}
                        <Route path="/analytics" element={<PrivateRoute><UserAnalytics /></PrivateRoute>} />

                        {/* Auction Invitation Route */}
                        <Route path="/auctions/:auctionSlug/invite/:invitationCode" element={<PrivateRoute><AuctionInvitation /></PrivateRoute>} />

                        {/* Pending Invitations Route */}
                        <Route path="/invitations" element={<PrivateRoute><PendingInvitations /></PrivateRoute>} />
                        <Route path="/invitations/accepted" element={<PrivateRoute><AcceptedInvitations /></PrivateRoute>} />
                        <Route path="/invitations/sent" element={<PrivateRoute><SentInvitations /></PrivateRoute>} />

                        <Route path="/auctions/:auctionSlug/invitation/:invitationCode" element={<AuctionInvitationPage />} />

                        {/* 404 Page - Catch all unmatched routes */}
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Routes>
            </Router>
        </ConfigProvider>
    );
};
export default App;


